import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useApi from '../hooks/useApi';
import { PROGRAMS } from '../constants/programs';
import ConfirmDialog from '../components/ConfirmDialog';
import EmailSendDetailDialog from '../components/emailAlerts/EmailSendDetailDialog';
import { DailyDigestSectionTable, SECTION_CONFIG } from '../components/dailyDigest/DailyDigestSectionTable';

const ALL_PROGRAMS = 'All Programs';
const DEFAULT_PAGE_SIZE = 50;
const DB_LOG_LEVEL_OPTIONS = [
  { value: 'warning,error', label: 'Warning + Error' },
  { value: 'error', label: 'Error only' },
  { value: 'warning,warn', label: 'Warning only' },
  { value: '', label: 'All levels' },
];

const DailyDigestSectionPage = () => {
  const { section } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { useDailyDigestSection, useEmailTriggerSendDetail, useRetryEmailTriggerSend } = useApi();

  const config = SECTION_CONFIG[section] || null;
  const isDbErrorLogs = section === 'dbErrorLogs';
  const programOptions = [ALL_PROGRAMS, ...PROGRAMS];

  const [programName, setProgramName] = useState(searchParams.get('programName') || ALL_PROGRAMS);
  const [bac, setBac] = useState(searchParams.get('bac') || '');
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [level, setLevel] = useState(searchParams.get('level') || 'warning,error');
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [selectedSendId, setSelectedSendId] = useState(null);
  const [retryState, setRetryState] = useState({ open: false, id: null });

  const queryOptions = useMemo(() => {
    return {
      section,
      days: 180,
      programName: programName === ALL_PROGRAMS ? null : programName,
      bac: bac || null,
      search: search || null,
      level: isDbErrorLogs ? level || null : null,
      limit: pagination.pageSize,
      offset: pagination.pageIndex * pagination.pageSize,
    };
  }, [section, programName, bac, search, level, pagination, isDbErrorLogs]);

  const { data, isLoading, error } = useDailyDigestSection(queryOptions);
  const { data: sendDetail, isLoading: sendDetailLoading } = useEmailTriggerSendDetail(selectedSendId);
  const retrySend = useRetryEmailTriggerSend();

  const rows = Array.isArray(data?.rows) ? data.rows : [];
  const rowCount = Number.isFinite(Number(data?.totalRows)) ? Number(data.totalRows) : rows.length;

  const confirmRetry = async () => {
    if (!retryState.id) return;
    try {
      await retrySend.mutateAsync({ id: retryState.id, options: { forceSend: true } });
      setRetryState({ open: false, id: null });
    } catch (_error) {
      setRetryState({ open: false, id: null });
    }
  };

  if (!config) {
    return (
      <Alert severity="error">Unknown digest section.</Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              {config.title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Expanded audit view (last 180 days)
            </Typography>
          </Box>
          <Chip label="180 days" variant="outlined" />
          <Button variant="outlined" onClick={() => navigate('/story')}>
            Back to Daily Digest
          </Button>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Program</InputLabel>
            <Select value={programName} label="Program" onChange={(event) => {
              setProgramName(event.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}>
              {programOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="BAC"
            value={bac}
            onChange={(event) => {
              setBac(event.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
          />
          <TextField
            size="small"
            label="Search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder="Search (ex: BAC 321985)"
            sx={{ flex: 1 }}
          />
          {isDbErrorLogs && (
            <FormControl size="small" sx={{ minWidth: 170 }}>
              <InputLabel>Level</InputLabel>
              <Select
                value={level}
                label="Level"
                onChange={(event) => {
                  setLevel(event.target.value);
                  setPagination((prev) => ({ ...prev, pageIndex: 0 }));
                }}
              >
                {DB_LOG_LEVEL_OPTIONS.map((option) => (
                  <MenuItem key={option.value || 'all'} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Stack>

        {error && <Alert severity="error">{error?.message || 'Failed to load section data.'}</Alert>}
        <DailyDigestSectionTable
          section={section}
          rows={rows}
          rowCount={rowCount}
          isLoading={isLoading}
          pagination={pagination}
          onPaginationChange={(updater) => {
            const next = typeof updater === 'function' ? updater(pagination) : updater;
            setPagination(next);
          }}
          onInspectSend={section === 'triggerExecutions' ? (row) => setSelectedSendId(row.id) : null}
          onRetrySend={section === 'triggerExecutions' ? (row) => setRetryState({ open: true, id: row.id }) : null}
        />
      </Paper>

      {section === 'triggerExecutions' && (
        <>
          <EmailSendDetailDialog
            open={Boolean(selectedSendId)}
            detail={sendDetail}
            loading={sendDetailLoading}
            onClose={() => setSelectedSendId(null)}
            onRetry={() => setRetryState({ open: true, id: selectedSendId })}
            retrying={retrySend.isPending}
            showRetry={String(sendDetail?.status || '').toLowerCase() === 'failed'}
          />
          <ConfirmDialog
            open={retryState.open}
            title="Retry email send?"
            description="This will re-render and send the email again."
            confirmLabel="Retry"
            confirmColor="primary"
            loading={retrySend.isPending}
            onConfirm={confirmRetry}
            onCancel={() => setRetryState({ open: false, id: null })}
          />
        </>
      )}
    </Box>
  );
};

export default DailyDigestSectionPage;
